import React, { useState } from "react";

import { Table, Input, Space, Button, InputNumber } from "antd";
import { SearchOutlined, FilterOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import dayjs from "dayjs";

import { RankStats, TimedRankStats } from "../../types/api";
import { useAntdTheme } from "../../shared/hooks";

export type TimedRankStatsTableProps = {
  data: TimedRankStats;
};

type FlatTimedRankStats = RankStats & { timestamp: string };

export const TimedRankStatsTable = (props: TimedRankStatsTableProps) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  // Flatten into list of rows.
  let rows: Array<FlatTimedRankStats> = [];
  Object.entries(props.data).forEach(([ts, stats]) => {
    let row: FlatTimedRankStats = { ...stats, timestamp: ts };
    rows.push(row);
  });

  const getNumericFilterProps = (dataIndex: keyof FlatTimedRankStats, title: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => {
      const [min, max] = selectedKeys[0]
        ? selectedKeys[0].split("-").map(Number)
        : [undefined, undefined];
      return (
        <div style={{ padding: token.marginSM }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <InputNumber
              placeholder={`${t("min")} ${title}`}
              value={min}
              onChange={(value) => {
                const newMax = max ?? "";
                setSelectedKeys(
                  value !== null && value !== undefined ? [`${value}-${newMax}`] : []
                );
              }}
              style={{
                width: "100%",
                borderRadius: token.borderRadiusLG,
              }}
            />
            <InputNumber
              placeholder={`${t("max")} ${title}`}
              value={max}
              onChange={(value) => {
                const newMin = min ?? "";
                setSelectedKeys(
                  value !== null && value !== undefined ? [`${newMin}-${value}`] : []
                );
              }}
              style={{
                width: "100%",
                borderRadius: token.borderRadiusLG,
              }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  confirm();
                  setSearchedColumn(dataIndex);
                }}
                icon={<FilterOutlined />}
                size="small"
                style={{
                  width: 90,
                  borderRadius: token.borderRadius,
                }}
              >
                {t("filter")}
              </Button>
              <Button
                onClick={() => {
                  if (clearFilters) {
                    clearFilters();
                  }
                }}
                size="small"
                style={{
                  width: 90,
                  borderRadius: token.borderRadius,
                }}
              >
                {t("cancel")}
              </Button>
            </Space>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered: boolean) => (
      <FilterOutlined style={{ color: filtered ? token.colorPrimary : undefined }} />
    ),
    onFilter: (value: string, record: FlatTimedRankStats) => {
      const [min, max] = value.split("-").map(Number);
      const recordValue = record[dataIndex] as number;
      if (min !== undefined && !isNaN(min) && max !== undefined && !isNaN(max)) {
        return recordValue >= min && recordValue <= max;
      } else if (min !== undefined && !isNaN(min)) {
        return recordValue >= min;
      } else if (max !== undefined && !isNaN(max)) {
        return recordValue <= max;
      }
      return true;
    },
  });

  const columns: any = [
    {
      title: "Time",
      dataIndex: "timestamp",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Backend returns timestamps already converted to local timezone
        return dayjs(text).format("MM/DD");
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        const at = dayjs(a.timestamp);
        const bt = dayjs(b.timestamp);
        if (at.isBefore(bt)) {
          return -1;
        } else if (at.isSame(bt)) {
          return 0;
        }
        return 1;
      },
    },
    {
      title: "Average",
      dataIndex: "avg",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.avg - b.avg;
      },
      ...getNumericFilterProps("avg", "Average"),
    },
    {
      title: "p50",
      dataIndex: "p50",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.p50 - b.p50;
      },
      ...getNumericFilterProps("p50", "p50"),
    },
    {
      title: "p95",
      dataIndex: "p95",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.p95 - b.p95;
      },
      ...getNumericFilterProps("p95", "p95"),
    },
    {
      title: "p99",
      dataIndex: "p99",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.p99 - b.p99;
      },
      ...getNumericFilterProps("p99", "p99"),
    },
    {
      title: "Min",
      dataIndex: "min",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.min - b.min;
      },
      ...getNumericFilterProps("min", "Min"),
    },
    {
      title: "Max",
      dataIndex: "max",
      defaultSortOrder: "ascend",
      sortDirections: ["ascend", "descend"],
      render: (text: string, record: FlatTimedRankStats, index: string) => {
        // Round to 2 decimal places
        return parseFloat(text).toFixed(2);
      },
      sorter: (a: FlatTimedRankStats, b: FlatTimedRankStats) => {
        return a.max - b.max;
      },
      ...getNumericFilterProps("max", "Max"),
    },
  ];

  return (
    <div>
      <Table
        columns={columns}
        dataSource={rows}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        style={{ borderRadius: token.borderRadiusLG }}
      />
    </div>
  );
};
